import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recycleBinApi, DeletedEntity } from '../api/recycleBin';
import PremiumLayout from '../components/PremiumLayout';
import { 
  TrashIcon, 
  ArrowPathIcon, 
  XMarkIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ChartBarIcon,
  CalendarIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';

type EntityType = 'lead' | 'account' | 'contact' | 'activity' | 'task';

const RecycleBin: React.FC = () => {
  const [selectedType, setSelectedType] = useState<EntityType>('lead');
  const queryClient = useQueryClient();

  const { data: deletedItems = [], isLoading } = useQuery({
    queryKey: ['recycle-bin', selectedType],
    queryFn: () => recycleBinApi.getDeleted(selectedType),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: number) => recycleBinApi.restore(selectedType, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recycle-bin'] });
    },
  });

  const permanentDeleteMutation = useMutation({
    mutationFn: (id: number) => recycleBinApi.permanentDelete(selectedType, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recycle-bin'] });
    },
  });

  const handleRestore = (id: number) => {
    if (window.confirm('Are you sure you want to restore this item?')) {
      restoreMutation.mutate(id);
    }
  };

  const handlePermanentDelete = (id: number) => {
    if (window.confirm('Are you sure you want to permanently delete this item? This action cannot be undone.')) {
      permanentDeleteMutation.mutate(id);
    }
  };

  const getEntityIcon = (type: EntityType) => {
    switch (type) {
      case 'lead': return <ChartBarIcon className="h-5 w-5" />;
      case 'account': return <BuildingOfficeIcon className="h-5 w-5" />;
      case 'contact': return <UserGroupIcon className="h-5 w-5" />;
      case 'activity': return <CalendarIcon className="h-5 w-5" />;
      case 'task': return <ClipboardDocumentListIcon className="h-5 w-5" />;
    }
  };

  const entityTypes: { type: EntityType; label: string }[] = [
    { type: 'lead', label: 'Leads' },
    { type: 'account', label: 'Accounts' },
    { type: 'contact', label: 'Contacts' },
    { type: 'activity', label: 'Activities' },
    { type: 'task', label: 'Tasks' },
  ];

  if (isLoading) {
    return (
      <PremiumLayout title="Recycle Bin">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout
      title="Recycle Bin"
      subtitle="Restore or permanently delete deleted items"
    >
      <div className="space-y-6">
        {/* Entity Type Selector */}
        <div className="premium-card p-4">
          <div className="flex space-x-2">
            {entityTypes.map(({ type, label }) => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedType === type
                    ? 'bg-indigo-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {getEntityIcon(type)}
                <span className="ml-2">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Deleted Items List */}
        <div className="premium-card p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            Deleted {entityTypes.find(t => t.type === selectedType)?.label} ({deletedItems.length})
          </h2>

          {deletedItems.length === 0 ? (
            <div className="text-center py-12">
              <TrashIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No deleted {selectedType}s found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Deleted At</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {deletedItems.map((item: DeletedEntity) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.name || `Item #${item.id}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(item.deleted_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleRestore(item.id)}
                          className="text-indigo-600 hover:text-indigo-900 flex items-center"
                        >
                          <ArrowPathIcon className="h-4 w-4 mr-1" />
                          Restore
                        </button>
                        <button
                          onClick={() => handlePermanentDelete(item.id)}
                          className="text-red-600 hover:text-red-900 flex items-center"
                        >
                          <XMarkIcon className="h-4 w-4 mr-1" />
                          Delete Permanently
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </PremiumLayout>
  );
};

export default RecycleBin;
